"use client";

import { MouseEvent, useEffect, useRef, useState } from "react";
import { getSearchResultClient } from "@/entities/search/api/search.client";
import { hospitalDirectoryPath, HospitalDirectoryRoute } from "@/features/hospital-directory/lib/directory";
import { buildHospitalDirectoryListItems, HospitalDirectoryListItem, HospitalDirectorySearchScope } from "@/features/hospital-directory/lib/directoryList";
import HospitalDirectoryCard from "@/widgets/hospital-directory/ui/HospitalDirectoryCard";
type HospitalDirectoryLoadMoreProps = {
  locale: string;
  route: HospitalDirectoryRoute;
  totalPages: number;
  // 서버가 첫 페이지를 부를 때 쓴 범위 그대로 — 어긋나면 다른 허브 결과가 이어 붙는다
  scope: HospitalDirectorySearchScope;
  categoryLabel?: string;
};

/**
 * 시안의 가로 꽉 찬 "더보기". 점진적 향상으로 만든다:
 * JS가 없거나 크롤러면 그냥 다음 페이지로 가는 <a href>이고, JS가 있으면 이동 대신
 * 다음 페이지를 이어 붙인다. 번호 페이지네이션이 sitemap에 없는 2~N페이지의 유일한
 * 크롤 경로라, 이 버튼은 그 경로를 대체하는 게 아니라 같은 URL을 가리키는 링크여야 한다.
 *
 * 서버 렌더된 첫 30건(목록 본문)은 건드리지 않는다. 이 컴포넌트는 목록 아래
 * 버튼과 이어 붙인 카드만 담당한다.
 */
const HospitalDirectoryLoadMore = ({
  locale,
  route,
  totalPages,
  scope,
  categoryLabel
}: HospitalDirectoryLoadMoreProps) => {
  // 지금까지 화면에 붙은 마지막 페이지. 초기값은 서버가 렌더한 페이지다.
  const [loadedPage, setLoadedPage] = useState(route.page);
  const [appendedItems, setAppendedItems] = useState<HospitalDirectoryListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);
  const buttonRef = useRef<HTMLAnchorElement>(null);
  const appendedListRef = useRef<HTMLUListElement>(null);
  // 마지막 페이지를 불러오면 포커스돼 있던 버튼이 사라진다. 그대로 두면 activeElement가
  // body로 떨어져 키보드 사용자가 위치를 잃으므로, 방금 붙은 첫 카드로 포커스를 넘긴다.
  const [focusFromIndex, setFocusFromIndex] = useState<number | null>(null);
  useEffect(() => {
    if (focusFromIndex === null) return;
    appendedListRef.current?.querySelectorAll("a")[focusFromIndex]?.focus();
  }, [focusFromIndex]);
  const nextPage = loadedPage + 1;
  const hasNextPage = nextPage <= totalPages;
  const nextHref = hospitalDirectoryPath(locale, {
    ...route,
    page: nextPage
  });
  const onLoadMore = async (event: MouseEvent<HTMLAnchorElement>) => {
    // 새 탭/새 창 의도(⌘·Ctrl·Shift·Alt·가운데 클릭)는 가로채지 않고 링크 그대로 둔다
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) return;
    // 직전 시도가 실패했으면 가로채지 않는다 — 진짜 href로 다음 페이지에 이동시킨다.
    // 이어붙이기가 안 되는 사용자를 버튼에 가두지 않는 것이 점진적 향상의 핵심이다.
    if (hasFailed) return;
    event.preventDefault();
    if (isLoading) return;
    setIsLoading(true);
    try {
      const response = await getSearchResultClient({
        ...scope,
        page: nextPage
      });
      const items = buildHospitalDirectoryListItems({
        hospitals: response.result.hospitals,
        // 순번은 전역이다 — 2페이지 첫 카드가 31이 되도록 서버 카드와 같은 함수를 쓴다
        page: nextPage,
        locale
      });
      // 버튼이 사라질 차례이고 그 버튼이 포커스를 쥐고 있었다면, 새로 붙는 첫 카드로 넘긴다
      if (nextPage >= totalPages && document.activeElement === buttonRef.current) {
        setFocusFromIndex(appendedItems.length);
      }
      setAppendedItems(previous => [...previous, ...items]);
      setLoadedPage(nextPage);
    } catch {
      setHasFailed(true);
    } finally {
      setIsLoading(false);
    }
  };

  // 이어붙인 목록은 어떤 상태에서도 사라지면 안 된다. 마지막 페이지에 닿으면
  // 버튼만 감춘다 — 컴포넌트째 null을 반환하면 방금 불러온 카드가 눈앞에서 없어진다.
  if (!hasNextPage && appendedItems.length === 0) return null;
  const statusMessage = isLoading ? "다음 페이지를 불러오는 중입니다." : hasFailed ? "목록을 불러오지 못했습니다. 다시 누르면 다음 페이지로 이동합니다." : appendedItems.length > 0 ? `병원 ${appendedItems.length}곳을 추가로 불러왔습니다.${hasNextPage ? "" : " 마지막 페이지입니다."}` : "";
  return <>
      {appendedItems.length > 0 ? <ul ref={appendedListRef} aria-label="더보기로 불러온 병원" className="mt-2 flex flex-col gap-2">
          {appendedItems.map(item => <li key={item.hospitalOriginId}>
              <HospitalDirectoryCard locale={locale} item={item} categoryLabel={categoryLabel} />
            </li>)}
        </ul> : null}

      {hasNextPage ? <a ref={buttonRef} href={nextHref} onClick={onLoadMore} aria-busy={isLoading} data-testid="directory-load-more" className="mt-3 block w-full rounded-xl bg-primary-600 px-4 py-3.5 text-center text-base font-semibold text-white">
          {isLoading ? "불러오는 중…" : "더보기"}
        </a> : null}

      {/* 로딩·추가 건수·완료·실패를 모두 알린다. 내용이 생기기 전부터 DOM에 있어야 읽힌다. */}
      <p role="status" aria-live="polite" className="sr-only">{statusMessage}</p>

      {hasFailed ? <p className="mt-2 text-center text-xs text-[#8B8D94]">
          목록을 불러오지 못했습니다. 다시 누르면 다음 페이지로 이동합니다.
        </p> : null}
    </>;
};
export default HospitalDirectoryLoadMore;